import { useState, useEffect } from "react";

import { withTranslation } from "react-i18next";
import { Box, InputAdornment, TextField } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar"
import Dialog from "@plannel/components/Dialog";
import { isEmpty, isEqual } from "lodash";
import psiSimulationService from "@plannel/services/rp/psi-simulation-service"

const RpPsiSimulationEditModal = (props) => {
  const { t, open, setOpen, title, rpPlanVersion, selectedRow, retrievePsiSimulation } = props;

  const modalTitle = isEmpty(title) ? t("rpConfiguration") : title;
  const { snackBarMessageColor } = ColorUtil;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [initalConfigs, setInitalConfigs] = useState({});

  const [otdLtDays, setOtdLtDays] = useState(0);
  const [moq, setMoq] = useState(0);
  const [ioq, setIoq] = useState(0);
  const [destLocName, setDestLocName] = useState("");
  const [originLocName, setOriginLocName] = useState("");
  const [itemCd, setItemCd] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemHrchyName, setItemHrchyName] = useState("");
  const [abcXyzClass, setAbcXyzClass] = useState("");

  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal)) || numVal < 0;
  }
  
  const initalizeConfigs = () => {
    setMoq(initalConfigs.moq ?? 0);
    setIoq(initalConfigs.ioq ?? 0);
    setOtdLtDays(initalConfigs.otdLtDays ?? 0);
  };

  const handleSave = () => {

    if (selectedRow !== null && selectedRow.length > 0) {
      let configObj = {};
    
      configObj = { ...selectedRow[0] };
      configObj.planVersion = rpPlanVersion;
      configObj.otdLtDays = otdLtDays;
      configObj.moq = moq;
      configObj.ioq = ioq;
      configObj.aggVerNum = configObj?.aggVerNumPivotData[0];
      configObj.aggId = configObj?.aggIdPivotData[0];

      if (checkValidation(moq) || checkValidation(ioq) || checkValidation(otdLtDays)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_InvalidValue") });

        return false;
      } 
      
      psiSimulationService.upsertRpPolicy(configObj)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setOpen(false);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        retrievePsiSimulation();
      });
    
    }

    return true;
  };

  const handleClose = (action) => {
    if (isEqual(action, "cancel")) {
      setOpen(false);
      initalizeConfigs();
    } else if (isEqual(action, "save")) {
      handleSave();
    }
  };

  
  const handleDialog = (action) => {
    if (action === "SAVE") {
      handleSave();
    }
  };

  useEffect(() => {
    if (selectedRow && open) {

      setOtdLtDays(selectedRow[0]?.otdLt);
      setMoq(selectedRow[0]?.moq);
      setIoq(selectedRow[0]?.ioq);
      setOriginLocName(selectedRow[0]?.originLocNm);
      setDestLocName(selectedRow[0]?.destLocNm);
      setItemCd(selectedRow[0]?.itemCd);
      setItemName(selectedRow[0]?.itemNm);
      setItemHrchyName(selectedRow[0]?.categoryNm);
      setAbcXyzClass(selectedRow[0]?.invClassCd);

      const configObj = {};

      configObj.otdLtDays = selectedRow[0]?.otdLt;
      configObj.moq = selectedRow[0]?.moq;
      configObj.ioq = selectedRow[0]?.ioq;
      setInitalConfigs(configObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("days")}</InputAdornment> } : {};
  }

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={() => handleClose("cancel")}
        title={modalTitle}
        onSave={() => handleClose("save")}
        width={452}>
          <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                name="originLocName"
                label={t("originLocation")}
                value={originLocName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="destLocName"
                label={t("destLocation")}
                value={destLocName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                name="itemCd"
                label={t("itemCd")}
                value={itemCd || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="itemName"
                label={t("itemName")}
                value={itemName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                name="itemHrchyName"
                label={t("category")}
                value={itemHrchyName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="abcXyzClass"
                label={t("invClassCd")}
                value={abcXyzClass || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", mt: 2 }}>
            <TextField
              size="small"
              name="moq"
              label={t("moq")}
              value={moq}
              sx={{ maxWidth: 120 }}
              required
              error={checkValidation(moq)}
              onChange={e => setMoq(e.target.value)}
            />
            <TextField
              size="small"
              name="otdLtDays"
              label={t("ioq")}
              value={ioq}
              sx={{ maxWidth: 120, ml: 1 }}
              required
              error={checkValidation(ioq)}
              onChange={e => setIoq(e.target.value)}
            />
            <TextField
              size="small"
              name="otdLtDays"
              label={t("otdLt")}
              value={otdLtDays}
              sx={{ maxWidth: 160, ml: 1 }}
              required
              error={checkValidation(otdLtDays)}
              InputProps={inputPropsByValue(otdLtDays)}
              onChange={e => setOtdLtDays(e.target.value)}
            />
          </Box>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  )
};

export default withTranslation()(RpPsiSimulationEditModal);